import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import StarRating from "@/components/base/StarRating";
import EmptyState from "@/components/base/EmptyState";
import DetailActions from "@/pages/detail/components/DetailActions";
import RelatedMovies from "@/pages/detail/components/RelatedMovies";
import { getCategoryById, getMovieById, getRelatedMovies, formatViews } from "@/utils/movie";
import { useSeo } from "@/hooks/useSeo";

function InfoItem({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-3.5">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
        <i className={`${icon} text-base`} aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <p className="text-xs text-foreground-500">{label}</p>
        <p className="mt-0.5 text-sm font-medium text-foreground-900">{value}</p>
      </div>
    </div>
  );
}

export default function DetailPage() {
  const { movieId = "" } = useParams();
  const movie = getMovieById(movieId);

  useSeo({
    title: movie ? `【${movie.title}】剧情介绍 - 韩国三级电影` : "电影详情 - 韩国三级电影",
    description: movie
      ? `${movie.title}：${movie.synopsis.slice(0, 70)}……导演 ${movie.director}，主演 ${movie.cast.join("、")}，${movie.year} 年上映。`
      : "韩国三级电影详情页，查看剧情简介、演职员信息与相关推荐。",
    keywords: movie
      ? `${movie.title},韩国三级电影,${movie.tags.join(",")},${movie.genres.join(",")},剧情介绍`
      : "韩国三级电影,电影详情,剧情介绍",
  });

  if (!movie) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6">
          <EmptyState
            icon="ri-film-line"
            title="未找到该影片"
            description="你访问的影片可能已被移除或链接有误，可返回首页浏览最新内容。"
            action={
              <Link
                to="/"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-primary-600"
              >
                返回首页
              </Link>
            }
          />
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedMovies(movie, 5);
  const primaryCategory = getCategoryById(movie.categoryIds[0]);

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: primaryCategory ? primaryCategory.name : "分类", to: `/category/${movie.categoryIds[0]}` },
            { label: movie.title },
          ]}
        />

        <section className="mt-4 overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="relative h-[220px] w-full md:h-[300px]">
            <img
              src={movie.backdrop ?? movie.cover}
              alt={`${movie.title} 剧照`}
              title={`${movie.title} - 韩国三级电影`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/40 to-transparent" />
          </div>

          <div className="relative px-4 pb-6 md:px-8 md:pb-8">
            <div className="-mt-16 flex flex-col gap-5 md:-mt-24 md:flex-row md:items-end">
              <div className="w-[130px] shrink-0 overflow-hidden rounded-lg border border-background-200 bg-background-200 shadow-none md:w-[170px]">
                <div className="aspect-[2/3] w-full">
                  <img
                    src={movie.cover}
                    alt={`${movie.title} 海报`}
                    title={`${movie.title} - 韩国三级电影`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="min-w-0 flex-1 pb-1">
                <h1 className="text-2xl font-black leading-tight text-foreground-950 md:text-3xl">
                  {movie.title}
                </h1>
                <p className="mt-1 text-sm text-foreground-500">{movie.originalTitle}（演示原名）</p>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-600">
                  <StarRating rating={movie.rating} />
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line text-base" aria-hidden="true" />
                    {movie.year} 年
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line text-base" aria-hidden="true" />
                    {movie.duration} 分钟
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line text-base" aria-hidden="true" />
                    {formatViews(movie.views)} 次浏览
                  </span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {movie.genres.map((g) => (
                    <span
                      key={g}
                      className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900"
                    >
                      {g}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-4 border-t border-background-200 pt-5 md:flex-row md:items-center md:justify-between">
              <DetailActions movieId={movie.id} movieTitle={movie.title} />
              <Link
                to={`/category/${movie.categoryIds[0]}`}
                className="flex w-fit items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors duration-200 hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-arrow-left-line text-base" aria-hidden="true" />
                返回 {primaryCategory ? primaryCategory.name : "分类"}
              </Link>
            </div>
          </div>
        </section>

        <section className="mt-6">
          <h2 className="flex items-center gap-3 text-lg font-bold text-foreground-950">
            <span className="h-5 w-1 rounded-full bg-primary-500" aria-hidden="true" />
            基本信息
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <InfoItem icon="ri-user-star-line" label="导演" value={movie.director} />
            <InfoItem icon="ri-group-line" label="主演" value={movie.cast.join("、")} />
            <InfoItem icon="ri-movie-2-line" label="类型" value={movie.genres.join(" / ")} />
            <InfoItem icon="ri-calendar-line" label="上映时间" value={`${movie.year} 年 · ${movie.duration} 分钟`} />
          </div>
        </section>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <section className="lg:col-span-2">
            <h2 className="flex items-center gap-3 text-lg font-bold text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-primary-500" aria-hidden="true" />
              剧情简介
            </h2>
            <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <p className="text-sm leading-7 text-foreground-700">{movie.synopsis}</p>
              <p className="mt-3 text-xs leading-relaxed text-foreground-400">
                以上剧情简介为演示内容，仅用于展示页面结构，实际作品信息以其官方资料为准。
              </p>
            </div>
          </section>

          <section>
            <h2 className="flex items-center gap-3 text-lg font-bold text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-primary-500" aria-hidden="true" />
              影片标签
            </h2>
            <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex flex-wrap gap-2">
                {[...movie.tags, ...movie.genres].map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-accent-100 px-3 py-1.5 text-xs font-medium text-accent-900 transition-colors duration-200 hover:bg-accent-200"
                  >
                    # {tag}
                  </Link>
                ))}
              </div>
              <p className="mt-4 text-xs leading-relaxed text-foreground-500">
                点击标签可查看包含相同关键词的其他作品。
              </p>
            </div>
          </section>
        </div>

        <div className="mt-10">
          <RelatedMovies movies={related} />
        </div>
      </div>
    </SiteLayout>
  );
}